import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Wine, Newspaper, ExternalLink, Navigation, Clock, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState, useCallback } from 'react';
import { lumi } from '../lib/lumi';
import { computeEventStatus } from '../utils/eventStatus';
import NewsletterSection from '../components/NewsletterSection';
import HomeCollectionGrid from '../components/HomeCollectionGrid';
import EditableText from '../components/EditableText';
import EditableImage from '../components/EditableImage';
import { useEditMode } from '../contexts/EditModeContext';
import SEO from '../components/SEO';

const HOME_SEO_IMAGE = 'https://static.lumi.new/44/4495c6feb2104e9b3b6d9f89ff4c7729.webp';

const Home = () => {
  const [homeContent, setHomeContent] = useState<any>({});
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isEditMode } = useEditMode();
  const [eventsContent, setEventsContent] = useState<any>({});
  const [selectedEvent, setSelectedEvent] = useState<any>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const contentResult = await lumi.entities.home_content.list({ limit: 50 });

      const content: any = {};
      contentResult.list?.forEach((item: any) => {
        content[item.section] = item;
      });

      // If an artwork is selected as the hero visual, use its image
      const heroArtworkId = content.hero?.heroArtworkId;
      if (heroArtworkId) {
        let heroArtwork: any = null;
        // Une erreur reseau ponctuelle ou une oeuvre supprimee ne doit pas casser l'accueil :
        // on retente une fois, sinon l'illustration deja enregistree reste utilisee.
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            const artworkResult = await lumi.entities.artwork.list({
              filter: { _id: heroArtworkId },
              limit: 1
            });
            heroArtwork = artworkResult.list?.[0] || null;
            break;
          } catch (err) {
            if (attempt === 1) {
              console.warn('Hero artwork unavailable, using saved hero image:', err);
            } else {
              await new Promise((resolve) => setTimeout(resolve, 600));
            }
          }
        }
        if (heroArtwork?.image) {
          content.hero = { ...content.hero, image: heroArtwork.image };
        }
      }

      setHomeContent(content);

      try {
        const [eventsResult, eventsContentResult] = await Promise.all([
        lumi.entities.events.list({ sort: { startDate: 1 }, limit: 50 }),
        lumi.entities.home_content.list({ filter: { section: 'events' }, limit: 1 })]
        );
        const allEvents = (eventsResult.list || []).filter((e: any) => e.isActive === true).map((e: any) => ({
          ...e,
          status: computeEventStatus(e.startDate, e.endDate, e.status)
        }));
        const upcoming = allEvents.filter((e: any) => e.type !== 'magazine' && (e.status === 'a_venir' || e.status === 'en_cours')).slice(0, 3);
        setUpcomingEvents(upcoming);
        if (eventsContentResult?.list?.[0]) {
          setEventsContent(eventsContentResult.list[0]);
        }
      } catch (err) {
        console.error('Error loading events:', err);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load page data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Helper to update local state after inline edit
  const handleFieldSave = (section: string, field: string, newValue: string) => {
    setHomeContent((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: newValue
      }
    }));
  };

  return (
    <div className="overflow-hidden">
      <SEO
        title="Studio RocheColombe — Artiste peintre contemporain"
        description="Studio RocheColombe — artiste peintre contemporain à Montélimar. Art abstrait et art cinétique, acrylique, résine, galerie d'art et expositions."
        keywords="Studio RocheColombe, artiste peintre, abstrait, art contemporain, art cinétique, résine, acrylique, galerie d'art, exposition, Montélimar"
        image={homeContent.hero?.image || HOME_SEO_IMAGE}
        url="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Studio RocheColombe — Artiste peintre contemporain | Oeuvres originales',
          url: 'https://www.studiorochecolombe.com/',
          description: 'Studio RocheColombe — peintures contemporaines originales, œuvres ondulatoires jouant avec la matière et la lumière. Studio-galerie à Montélimar, France.',
          isPartOf: {
            '@type': 'WebSite',
            name: 'Studio RocheColombe',
            url: 'https://www.studiorochecolombe.com/'
          },
          about: {
            '@type': 'Person',
            name: 'RocheColombe',
            alternateName: 'Studio RocheColombe'
          }
        }} />

      {/* Loading State */}
      {loading &&
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#722F37] mb-4"></div>
            <p className="text-[#2C2C2C]/60 font-['Roboto'] text-lg">Loading...</p>
          </div>
        </div>
      }

      {/* Error State */}
      {error && !loading &&
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6">
          <div className="max-w-md text-center">
            <div className="mb-6">
              <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="font-['Roboto'] text-2xl font-bold text-[#2C2C2C] mb-3">Unable to Load Page</h2>
            <p className="text-[#2C2C2C]/60 mb-6">{error}</p>
            <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#8C6D3F] text-white hover:bg-[#6B4E2E] transition-all duration-300">

              Retry
            </button>
          </div>
        </div>
      }

      {/* Main Content */}
      {!loading && !error &&
      <>
      {/* Hero Section - Full Screen */}
      <section className="relative min-h-[85vh] sm:min-h-screen flex items-center justify-center">
        {/* Page illustration only — no artwork visuals */}
        {(homeContent.hero?.image || isEditMode) &&
          <div className="absolute inset-0">
            {homeContent.hero?._id ?
            <EditableImage
              collection="home_content"
              documentId={homeContent.hero._id}
              field="image"
              value={homeContent.hero?.image || ''}
              alt="Studio RocheColombe"
              className="w-full h-full"
              imgClassName="w-full h-full object-cover"
              onSave={(v) => handleFieldSave('hero', 'image', v)} /> :

            homeContent.hero?.image ?
            <img
              src={homeContent.hero.image}
              alt="Studio RocheColombe"
              fetchpriority="high"
              className="w-full h-full object-cover" /> :

            null}
            <div className="absolute inset-0 bg-black/30" />
          </div>
          }

        {/* Hero Content */}
        <motion.div
            className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto py-16 sm:py-0"
            initial={{ opacity: 1, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}>

          <motion.div
              initial={{ opacity: 1, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}>
            <EditableText
                collection="home_content"
                documentId={homeContent.hero?._id || ''}
                field="title"
                value={homeContent.hero?.title || "STUDIO ROCHECOLOMBE"}
                type="text"
                className="font-['Roboto'] text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-4 sm:mb-6 leading-tight tracking-wider"
                onSave={(v) => handleFieldSave('hero', 'title', v)}
                as="h1">

              {homeContent.hero?.title || "STUDIO ROCHECOLOMBE"}
            </EditableText>
          </motion.div>

          {(homeContent.hero?.subtitle || isEditMode) &&
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.7 }}>
              <EditableText
                collection="home_content"
                documentId={homeContent.hero?._id || ''}
                field="subtitle"
                value={homeContent.hero?.subtitle || ''}
                type="text"
                className="text-base sm:text-xl md:text-2xl text-white/90 mb-3 sm:mb-4 tracking-widest uppercase font-light"
                onSave={(v) => handleFieldSave('hero', 'subtitle', v)}
                as="p">

                {homeContent.hero?.subtitle || (isEditMode ? 'Click to add subtitle...' : '')}
              </EditableText>
            </motion.div>
            }

          {(homeContent.hero?.description || isEditMode) &&
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.9 }}>
              <EditableText
                collection="home_content"
                documentId={homeContent.hero?._id || ''}
                field="description"
                value={homeContent.hero?.description || ''}
                type="textarea"
                className="text-base sm:text-lg text-white/80 mb-6 sm:mb-8 max-w-2xl mx-auto"
                onSave={(v) => handleFieldSave('hero', 'description', v)}
                as="div">

                {homeContent.hero?.description ?
                <span dangerouslySetInnerHTML={{ __html: homeContent.hero.description }} /> :
                isEditMode ?
                <span className="opacity-50 italic">Click to add description...</span> :
                null}
              </EditableText>
            </motion.div>
            }

          <motion.div
              initial={{ opacity: 1, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.1 }}>

            <EditableText
                collection="home_content"
                documentId={homeContent.hero?._id || ''}
                field="buttonText"
                value={homeContent.hero?.buttonText || '— Discover the collection'}
                type="text"
                onSave={(v) => handleFieldSave('hero', 'buttonText', v)}>

              







            </EditableText>
          </motion.div>
        </motion.div>
      </section>

      {/* Intro Section */}
      {((homeContent.intro?.title || homeContent.intro?.description) || isEditMode) &&
        <section className="py-14 sm:py-20 md:py-24 px-4 sm:px-6 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>

              <EditableText
                collection="home_content"
                documentId={homeContent.intro?._id || ''}
                field="title"
                value={homeContent.intro?.title || ''}
                type="text"
                className="font-['Roboto'] text-3xl sm:text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-4 sm:mb-6"
                onSave={(v) => handleFieldSave('intro', 'title', v)}
                as="h2">

                {homeContent.intro?.title || (isEditMode ? 'Click to add title...' : '')}
              </EditableText>

              {(homeContent.intro?.description || isEditMode) &&
              <EditableText
                collection="home_content"
                documentId={homeContent.intro?._id || ''}
                field="description"
                value={homeContent.intro?.description || ''}
                type="textarea"
                className="text-base sm:text-lg text-[#2C2C2C]/70 leading-relaxed prose prose-base sm:prose-lg max-w-none mx-auto"
                onSave={(v) => handleFieldSave('intro', 'description', v)}
                as="div">

                  {homeContent.intro?.description ?
                <span dangerouslySetInnerHTML={{ __html: homeContent.intro.description }} /> :
                isEditMode ?
                <span className="opacity-50 italic">Click to add introduction text...</span> :
                null}
                </EditableText>
              }
            </motion.div>
          </div>
        </section>
        }

      {/* About Section */}
      <section className="pt-10 sm:pt-14 md:pt-16 pb-8 sm:pb-12 md:pb-14 px-4 sm:px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>

            <div className="mb-6 sm:mb-8 flex justify-center">
              <img
                  src="https://static.lumi.new/44/4495c6feb2104e9b3b6d9f89ff4c7729.webp"
                  alt="Studio RocheColombe"
                  className="w-auto max-w-full object-contain"
                  style={{ height: 'clamp(72px, 12vw, 140px)' }}
                  loading="lazy"
                  decoding="async" />

            </div>

            {homeContent.about?.description || isEditMode ?
              <EditableText
                collection="home_content"
                documentId={homeContent.about?._id || ''}
                field="description"
                value={homeContent.about?.description || ''}
                type="textarea"
                className="text-lg text-[#2C2C2C]/70 mb-4 leading-relaxed prose prose-lg max-w-none mx-auto"
                onSave={(v) => handleFieldSave('about', 'description', v)}>

                {homeContent.about?.description ?
                <span dangerouslySetInnerHTML={{ __html: homeContent.about.description }} /> :

                <span className="opacity-50 italic">Click to add description...</span>
                }
              </EditableText> :

              <p className="text-lg text-[#2C2C2C]/70 mb-4 leading-relaxed text-[#1F2937]">Studio RocheColombe was born from a fascination with material — not as a flat, uniform surface, but as something capable of movement, breath, and light.

Working from a studio and gallery at 114 rue Pierre Julien in Montélimar, in the south of France, RocheColombe transforms layers of paint and texture into undulatory compositions: surfaces that seem to ripple, fold, and catch the light like water frozen mid-motion.

Each piece begins with a question rather than a plan — how far can a material be pushed before it becomes expressive? The answer emerges slowly, through layering, texture work, and patience, until the painting finds its own rhythm.</p>}


            <Link
                to="/about"
                className="inline-flex items-center gap-2 min-h-[44px] px-2 text-[12px] tracking-[0.18em] uppercase text-[#722F37] hover:text-[#B5623A] transition-colors group">

              More
              <ArrowRight
                  size={14}
                  className="transition-transform duration-300 group-hover:translate-x-1" />

            </Link>
          </motion.div>
        </div>
      </section>

      {/* Collection : oeuvres ayant une categorie, par 4 */}
      <HomeCollectionGrid />

      {/* Newsletter Section */}
      <NewsletterSection />

      {/* Upcoming Events Section */}
      {upcomingEvents.length > 0 &&
        <section className="py-14 sm:py-20 md:py-24 px-4 sm:px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-14">

              <EditableText
                collection="home_content"
                documentId={eventsContent?._id || ''}
                field="subtitle"
                value={eventsContent?.subtitle || 'Agenda'}
                type="text"
                className="text-[#8B7355] text-sm tracking-[0.3em] uppercase mb-3 block"
                onSave={(v) => setEventsContent((prev: any) => ({ ...prev, subtitle: v }))}
                as="span">

                {eventsContent?.subtitle || 'Agenda'}
              </EditableText>
              <EditableText
                collection="home_content"
                documentId={eventsContent?._id || ''}
                field="title"
                value={eventsContent?.title || 'Upcoming Events'}
                type="text"
                className="font-['Roboto'] text-3xl sm:text-4xl md:text-5xl font-bold text-[#2C2C2C]"
                onSave={(v) => setEventsContent((prev: any) => ({ ...prev, title: v }))}
                as="h2">

                {eventsContent?.title || 'Upcoming Events'}
              </EditableText>
            </motion.div>

            <div className="space-y-6">
              {upcomingEvents.map((event: any, index: number) => {
                const hasDateRange = event.startDate && event.endDate;
                const startDate = hasDateRange ? new Date(event.startDate) : null;
                const endDate = hasDateRange ? new Date(event.endDate) : null;
                const isOngoing = hasDateRange && computeEventStatus(event.startDate, event.endDate, event.status) === 'en_cours';
                const isMagazine = event.type === 'magazine';
                const hasMagazineCover = isMagazine && !!event.magazineCoverImage;
                const hasEventImage = !isMagazine && !!event.image;
                const hasLink = !!event.externalLink;

                return (
                  <motion.div
                    key={event._id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.15) }}
                    className="flex flex-col md:flex-row border border-[#D4C5B9]/30 bg-[#FAF9F6] hover:shadow-md transition-all duration-300 overflow-hidden">

                    {/* LEFT: Visual area */}
                    {isMagazine && hasMagazineCover ? (
                    /* Magazine: cover as main visual with article overlay */
                    <div className="relative flex-shrink-0 w-full md:w-52 lg:w-60 bg-[#F5F0EB] flex items-center justify-center p-5 md:p-6">
                        <a
                        href={hasLink ? event.externalLink : undefined}
                        target={hasLink ? '_blank' : undefined}
                        rel={hasLink ? 'noopener noreferrer' : undefined}
                        className={`relative z-10 block ${hasLink ? 'cursor-pointer' : 'cursor-default'}`}>

                          <div
                          className="w-32 lg:w-36 bg-white rounded-sm overflow-hidden transition-transform duration-500 hover:scale-[1.03]"
                          style={{
                            boxShadow: '6px 10px 24px rgba(0,0,0,0.16), 2px 3px 6px rgba(0,0,0,0.06)',
                            transform: 'rotate(-2deg)'
                          }}>

                            <img src={event.magazineCoverImage} alt={`${event.title} - Cover`} loading="lazy" decoding="async" className="w-full h-auto object-contain" />
                          </div>
                        </a>
                        {event.magazineArticleImage &&
                      <a
                        href={hasLink ? event.externalLink : undefined}
                        target={hasLink ? '_blank' : undefined}
                        rel={hasLink ? 'noopener noreferrer' : undefined}
                        className={`absolute bottom-2 right-2 md:bottom-3 md:right-3 z-20 block ${hasLink ? 'cursor-pointer' : 'cursor-default'}`}>

                            <div
                          className="w-20 lg:w-24 bg-white rounded-sm overflow-hidden transition-transform duration-500 hover:scale-[1.05]"
                          style={{
                            boxShadow: '3px 5px 16px rgba(0,0,0,0.18)',
                            transform: 'rotate(3deg)'
                          }}>

                              <img src={event.magazineArticleImage} alt={`${event.title} - Article`} loading="lazy" decoding="async" className="w-full h-auto object-contain" />
                            </div>
                          </a>
                      }
                        <div className="absolute top-2 left-2 z-30">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/90 backdrop-blur-sm text-[9px] font-semibold text-[#8B7355] tracking-wider uppercase rounded-sm shadow-sm">
                            <Newspaper size={10} />
                            Press
                          </span>
                        </div>
                      </div>) :
                    hasEventImage ? (
                    /* Exhibition: event image as left visual with date overlay, size adapts to the image */
                    <button
                      type="button"
                      onClick={() => setSelectedEvent(event)}
                      className="relative flex-shrink-0 w-full md:w-52 lg:w-60 flex items-center justify-center bg-[#F5F0EB] overflow-hidden group cursor-pointer">

                        <img
                        src={event.image}
                        alt={event.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-auto max-h-72 object-contain transition-transform duration-700 group-hover:scale-105" />

                        {startDate &&
                      <div className="absolute top-3 left-3 bg-[#722F37]/90 backdrop-blur-sm text-white px-2.5 py-1.5 text-center rounded-sm">
                            <span className="text-xl font-bold leading-none block">{startDate.toLocaleDateString('en-US', { day: 'numeric' })}</span>
                            <span className="text-[9px] tracking-[0.15em] uppercase text-white/80">{startDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</span>
                          </div>
                      }
                      </button>) :
                    startDate ? (
                    /* No image: date block */
                    <div className="flex-shrink-0 w-full md:w-24 bg-[#722F37] text-white flex flex-row md:flex-col items-center justify-center py-4 md:py-8 gap-2 md:gap-0">
                        <span className="text-3xl md:text-4xl font-bold leading-none">
                          {startDate.toLocaleDateString('en-US', { day: 'numeric' })}
                        </span>
                        <span className="text-xs tracking-[0.2em] uppercase text-white/80">
                          {startDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}
                        </span>
                      </div>) :
                    null}

                    {/* RIGHT: Content */}
                    <div className="flex-1 p-5 md:p-6 flex flex-col justify-center">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {isOngoing ?
                        <span className="px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Ongoing
                          </span> :

                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            Upcoming
                          </span>
                        }
                        {isMagazine &&
                        <span className="px-2 py-0.5 text-xs font-medium bg-[#F5F0EB] text-[#8B7355] border border-[#D4C5B9]/40">
                            Magazine
                          </span>
                        }
                      </div>

                      <button
                        type="button"
                        onClick={() => setSelectedEvent(event)}
                        className="text-left font-['Roboto'] text-xl md:text-2xl font-bold text-[#2C2C2C] mb-1 hover:text-[#722F37] transition-colors">

                        {event.title}
                      </button>
                      {event.titleFr && event.titleFr !== event.title &&
                      <p className="font-['Roboto'] italic text-sm text-[#722F37]/70 mb-1.5">
                          {event.titleFr}
                        </p>
                      }

                      {/* Description snippet (2 lines max) */}
                      {event.description &&
                      <p className="text-sm text-[#2C2C2C]/55 leading-relaxed mb-3 line-clamp-2">
                          {event.description}
                        </p>
                      }

                      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-[#2C2C2C]/60">
                        {startDate && endDate &&
                        <span className="flex items-center gap-1.5">
                            <Calendar size={13} className="text-[#8B7355]" />
                            {startDate.toLocaleDateString('en-US', { day: 'numeric', month: 'long' })} — {endDate.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </span>
                        }
                        {event.location && (
                        event.address ?
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            `${event.address}${event.city ? ', ' + event.city : ''}${event.country ? ', ' + event.country : ''}`
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-[#722F37] hover:text-[#5e2730] transition-colors cursor-pointer">

                              <MapPin size={13} />
                              {event.location}{event.city ? `, ${event.city}` : ''}
                              <Navigation size={11} className="ml-0.5 flex-shrink-0" />
                            </a> :

                        <span className="flex items-center gap-1.5">
                              <MapPin size={13} className="text-[#8B7355]" />
                              {event.location}{event.city ? `, ${event.city}` : ''}
                            </span>)

                        }
                        {event.openingHours &&
                        <span className="flex items-center gap-1.5">
                            <Clock size={13} className="text-[#8B7355]" />
                            {event.openingHours}
                          </span>
                        }
                        {event.vernissageDate &&
                        <span className="flex items-center gap-1.5 text-[#722F37]">
                            <Wine size={13} />
                            Vernissage: {new Date(event.vernissageDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long' })}
                            {event.vernissageTime ? ` \u00b7 ${event.vernissageTime}` : ''}
                          </span>
                        }
                      </div>
                      {event.externalLink &&
                      <a
                        href={event.externalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-[#722F37] hover:text-[#5e2730] transition-colors w-fit">

                          More information <ExternalLink size={13} />
                        </a>
                      }
                    </div>
                  </motion.div>);

              })}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center mt-10">

              <EditableText
                collection="home_content"
                documentId={eventsContent?._id || ''}
                field="buttonText"
                value={eventsContent?.buttonText || 'View all events'}
                type="text"
                onSave={(v) => setEventsContent((prev: any) => ({ ...prev, buttonText: v }))}>

                <Link
                  to={eventsContent?.buttonLink || '/actualites'}
                  className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#8C6D3F] text-[#8C6D3F] hover:bg-[#8C6D3F] hover:text-white transition-all duration-300"
                  onClick={(e) => isEditMode && e.preventDefault()}>

                  {eventsContent?.buttonText || 'View all events'}
                  <ArrowRight size={18} />
                </Link>
              </EditableText>
            </motion.div>
          </div>
        </section>
        }

      {/* Popup détail événement */}
      <AnimatePresence>
        {selectedEvent &&
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setSelectedEvent(null)}>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">

              {(selectedEvent.image || selectedEvent.magazineCoverImage) &&
              <div className="relative bg-[#F5F0EB] flex items-center justify-center">
                  <img
                  src={selectedEvent.image || selectedEvent.magazineCoverImage}
                  alt={selectedEvent.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full max-h-72 object-contain" />

                  <button
                  onClick={() => setSelectedEvent(null)}
                  className="absolute top-3 right-3 p-2 bg-white/90 hover:bg-white rounded-full transition-colors">

                    <X size={18} />
                  </button>
                </div>
              }
              <div className="p-6 md:p-8 relative">
                {!selectedEvent.image && !selectedEvent.magazineCoverImage &&
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors">

                    <X size={18} />
                  </button>
                }
                <div className="flex flex-wrap items-center gap-2.5 mb-3">
                  <span className="px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    {computeEventStatus(selectedEvent.startDate, selectedEvent.endDate, selectedEvent.status) === 'en_cours' ? 'En cours' : 'A venir'}
                  </span>
                  {selectedEvent.type === 'magazine' &&
                  <span className="px-2.5 py-1 text-xs font-medium bg-[#F5F0EB] text-[#8B7355] border border-[#D4C5B9]/40">
                      Magazine
                    </span>
                  }
                </div>

                <h3 className="font-['Roboto'] text-2xl md:text-3xl font-bold text-[#2C2C2C] mb-1">
                  {selectedEvent.title}
                </h3>
                {selectedEvent.titleFr && selectedEvent.titleFr !== selectedEvent.title &&
                <p className="font-['Roboto'] italic text-lg text-[#722F37]/70 mb-3">
                    {selectedEvent.titleFr}
                  </p>
                }

                {(selectedEvent.description || selectedEvent.descriptionFr) &&
                <div className="mb-5 space-y-2">
                    {selectedEvent.description &&
                  <p className="text-[#2C2C2C]/70 leading-relaxed">{selectedEvent.description}</p>
                  }
                    {selectedEvent.descriptionFr && selectedEvent.descriptionFr !== selectedEvent.description &&
                  <p className="text-[#2C2C2C]/55 leading-relaxed italic border-l-2 border-[#D4C5B9]/40 pl-3">
                        {selectedEvent.descriptionFr}
                      </p>
                  }
                  </div>
                }

                <div className="flex flex-wrap gap-3 mb-4">
                  {selectedEvent.startDate && selectedEvent.endDate &&
                  <span className="inline-flex items-center gap-1.5 text-sm text-[#2C2C2C]/60 bg-[#FAF9F6] rounded-md px-3 py-1.5">
                      <Calendar size={13} className="text-[#8B7355]" />
                      {new Date(selectedEvent.startDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} — {new Date(selectedEvent.endDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  }
                  {selectedEvent.location &&
                  <span className="inline-flex items-center gap-1.5 text-sm text-[#2C2C2C]/60 bg-[#FAF9F6] rounded-md px-3 py-1.5">
                      <MapPin size={13} className="text-[#8B7355]" />
                      {selectedEvent.location}
                      {selectedEvent.city && `, ${selectedEvent.city}`}
                    </span>
                  }
                  {selectedEvent.openingHours &&
                  <span className="inline-flex items-center gap-1.5 text-sm text-[#2C2C2C]/60 bg-[#FAF9F6] rounded-md px-3 py-1.5">
                      <Clock size={13} className="text-[#8B7355]" />
                      {selectedEvent.openingHours}
                    </span>
                  }
                  {selectedEvent.vernissageDate &&
                  <span className="inline-flex items-center gap-1.5 text-sm text-[#722F37]/80 bg-[#FDF6F7] rounded-md px-3 py-1.5 border border-[#722F37]/10">
                      <Wine size={13} className="text-[#722F37]" />
                      Vernissage : {new Date(selectedEvent.vernissageDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long' })}
                      {selectedEvent.vernissageTime && ` \u00b7 ${selectedEvent.vernissageTime}`}
                    </span>
                  }
                </div>

                {selectedEvent.address &&
                <div className="mb-5">
                    <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      `${selectedEvent.address}${selectedEvent.city ? ', ' + selectedEvent.city : ''}${selectedEvent.country ? ', ' + selectedEvent.country : ''}`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-[#722F37] hover:text-[#5e2730] hover:underline transition-colors">

                      <Navigation size={12} className="flex-shrink-0" />
                      {selectedEvent.address}
                      {selectedEvent.city && `, ${selectedEvent.city}`}
                      {selectedEvent.country && ` (${selectedEvent.country})`}
                    </a>
                  </div>
                }

                <div className="flex items-center gap-3">
                  {selectedEvent.externalLink &&
                  <a
                    href={selectedEvent.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-[#722F37] hover:text-[#5e2730] transition-colors border border-[#722F37]/20 px-4 py-2 rounded-md hover:bg-[#722F37]/5">

                      Plus d'informations <ExternalLink size={14} />
                    </a>
                  }
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="px-4 py-2 text-sm font-medium text-[#2C2C2C] border border-[#D4C5B9]/60 rounded-md hover:bg-[#FAF9F6] transition-colors">

                    Fermer
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
          }
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-14 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#EDEAE5]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>

            <EditableText
                collection="home_content"
                documentId={homeContent.cta?._id || ''}
                field="title"
                value={homeContent.cta?.title || 'Customize your space'}
                type="text"
                className="text-[18pt] font-light text-black/75 mb-4 sm:mb-6"
                onSave={(v) => handleFieldSave('cta', 'title', v)}
                as="h2">

              {homeContent.cta?.title || 'Customize your space'}
            </EditableText>

            {homeContent.cta?.description || isEditMode ?
              <EditableText
                collection="home_content"
                documentId={homeContent.cta?._id || ''}
                field="description"
                value={homeContent.cta?.description || ''}
                type="textarea"
                className="text-[14px] text-black/60 mb-7 leading-relaxed prose prose-sm max-w-none mx-auto"
                onSave={(v) => handleFieldSave('cta', 'description', v)}>

                {homeContent.cta?.description ?
                <span dangerouslySetInnerHTML={{ __html: homeContent.cta.description }} /> :

                <span className="opacity-50 italic">Click to add description...</span>
                }
              </EditableText> :

              <p className="text-[14px] text-black/60 mb-7 leading-relaxed">
                Contact us for any questions about available paintings, custom commissions, or studio visits.
              </p>
              }

            <Link
                to={homeContent.cta?.buttonLink || '/contact'}
                className="inline-flex items-center justify-center px-5 py-2 text-[11px] tracking-[0.14em] uppercase bg-black text-white hover:bg-black/80 transition-colors duration-300">

              {homeContent.cta?.buttonText || 'Contact us'}
            </Link>
          </motion.div>
        </div>
      </section>
        </>
      }
    </div>);

};

export default Home;